import type { CashflowStatement } from '../../types/api';
import type { SankeyLink, SankeyNode } from '../../components/charts/SankeyChart';
import { cashflowSankeyNodes } from '../../components/charts/config';
import type { MonthOption } from '../../components/charts/MonthRangeSlider';

export function extractMonthYear(dateStr: string): string {
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) return dateStr;
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    return `${year}${month.padStart(2, '0')}`;
  }
  return dateStr;
}

export function formatMonthYearLabel(monthYear: string): string {
  if (monthYear.length === 6 && /^\d{6}$/.test(monthYear)) {
    const year = monthYear.substring(0, 4);
    const month = parseInt(monthYear.substring(4, 6), 10);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${year}`;
  }
  return monthYear;
}

export function extractAvailableMonthsAsc(data: CashflowStatement[]): MonthOption[] {
  const monthSet = new Set<string>();
  data.forEach((item) => {
    const monthYear = extractMonthYear(item.calendar_date);
    if (monthYear && monthYear.length === 6) monthSet.add(monthYear);
  });
  return Array.from(monthSet)
    .sort((a, b) => a.localeCompare(b))
    .map((id) => ({ id, label: formatMonthYearLabel(id) }));
}

export function filterCashflowByRange(
  data: CashflowStatement[],
  startId: string,
  endId: string
): CashflowStatement[] {
  if (!startId || !endId || startId > endId) return data;
  return data.filter((item) => {
    const m = extractMonthYear(item.calendar_date);
    return m >= startId && m <= endId;
  });
}

export function buildSankeyFromCashflow(filteredData: CashflowStatement[]): {
  nodes: SankeyNode[];
  links: SankeyLink[];
} {
  const nodes: SankeyNode[] = cashflowSankeyNodes.map((node) => ({ ...node }));

  let openingBalance = 0;
  let rentalIncome = 0;
  let otherIncome = 0;
  let cashDeposits = 0;
  let operatingExpenses = 0;
  let capex = 0;
  let financingOutflows = 0;

  filteredData.forEach((item) => {
    const value = item.category_value || 0;
    if (item.category_code === 1) openingBalance += value;
    else if (item.category_code === 2) rentalIncome += value;
    else if (item.category_code === 3) otherIncome += value;
    else if (item.category_code === 4) cashDeposits += value;
    else if (item.category_code === 5) operatingExpenses += Math.abs(value);
    else if (item.category_code === 6) capex += Math.abs(value);
    else if (item.category_code === 7) financingOutflows += Math.abs(value);
  });

  const totalInflows = openingBalance + rentalIncome + otherIncome + cashDeposits;
  const closingBalance = totalInflows - operatingExpenses - capex - financingOutflows;

  const links: SankeyLink[] = [
    { source: 0, target: 4, value: Math.max(openingBalance, 0) },
    { source: 1, target: 4, value: Math.max(rentalIncome, 0) },
    { source: 2, target: 4, value: Math.max(otherIncome, 0) },
    { source: 3, target: 4, value: Math.max(cashDeposits, 0) },
    { source: 4, target: 5, value: Math.max(operatingExpenses, 0) },
    { source: 4, target: 6, value: Math.max(capex, 0) },
    { source: 4, target: 7, value: Math.max(financingOutflows, 0) },
    { source: 4, target: 8, value: Math.max(closingBalance, 0) },
  ].filter((link) => link.value > 0);

  return { nodes, links };
}
